/**
 * Per-person alert preferences (TMMAPP-0112; TMMAPI-0148, MM-D-119) — generated wire types in,
 * normalized domain out. `GET /v1/me/alert-preferences` serves the caller's default row per event
 * kind plus the effective matrix of every virtual account they can read, each row with its `source`
 * (the virtual account's own row, the person's default, or the system default). `PUT` replaces the
 * caller's own rows, all of them: the SPA sends the rows the person set and leaves every inherited
 * one out. tmm-api sends the alerts (instant mail or the 07:00 digest); the SPA only reads and
 * writes the preferences.
 */
import { request } from './client';
import type {
  AlertPreferenceInput as WireInput,
  AlertPreferencesRequest as WireRequest,
  AlertPreferencesResponse as WireResponse,
  AlertPreferenceView as WireView,
} from './generated';

/** The event kinds, in the Settings list's order. */
export const ALERT_EVENT_KINDS = [
  'ASSIGNMENTS',
  'BUDGET_CHANGED',
  'ENVELOPES_CHANGED',
  'VIRTUAL_ACCOUNT_NEGATIVE',
  'ENVELOPE_NEGATIVE',
  'LARGE_ALLOCATION_IN',
  'MEMBERS_CHANGED',
  'OWNER_ACCESS',
  'ASSISTANT_MESSAGES',
] as const;
export type AlertEventKind = (typeof ALERT_EVENT_KINDS)[number];

/**
 * Assistant messages (TMMAPI-0181 U6): the hourly digest of shared-session messages, `INSTANT` (on,
 * the default) or `OFF`. A switch on the defaults, never a per-virtual-account row.
 */
export const ASSISTANT_MESSAGES_KIND: AlertEventKind = 'ASSISTANT_MESSAGES';

export const ALERT_CADENCES = ['INSTANT', 'DAILY_DIGEST', 'OFF'] as const;
export type AlertCadence = (typeof ALERT_CADENCES)[number];

/** Where a row's value comes from: the virtual account's own row, the person's default, the system's. */
export type AlertSource = 'VIRTUAL_ACCOUNT' | 'DEFAULT' | 'SYSTEM';

/** The one event kind that takes a threshold (required unless OFF, above 0). */
export const THRESHOLD_KIND: AlertEventKind = 'LARGE_ALLOCATION_IN';

export interface AlertPreference {
  eventKind: AlertEventKind;
  cadence: AlertCadence;
  /** `LARGE_ALLOCATION_IN` only; minor units of the virtual account owner's currency. */
  thresholdMinor: number | null;
  source: AlertSource;
}

export interface VirtualAccountAlerts {
  virtualAccountId: number;
  name: string;
  preferences: AlertPreference[];
}

export interface AlertPreferences {
  defaults: AlertPreference[];
  virtualAccounts: VirtualAccountAlerts[];
}

const KINDS: ReadonlySet<string> = new Set(ALERT_EVENT_KINDS);
const CADENCES: ReadonlySet<string> = new Set(ALERT_CADENCES);
const SOURCES: ReadonlySet<string> = new Set(['VIRTUAL_ACCOUNT', 'DEFAULT', 'SYSTEM']);

/** A served row; `null` (dropped) for a kind or cadence this build doesn't know. */
export function normalizeAlertPreference(w: WireView): AlertPreference | null {
  if (!KINDS.has(w.eventKind)) return null;
  if (!CADENCES.has(w.cadence)) return null;
  return {
    eventKind: w.eventKind,
    cadence: w.cadence,
    thresholdMinor: w.thresholdMinor ?? null,
    source: SOURCES.has(w.source) ? w.source : 'SYSTEM',
  };
}

/** The rows in the list's kind order, one per kind (the first served wins). */
function normalizeRows(value: readonly WireView[]): AlertPreference[] {
  const byKind = new Map<AlertEventKind, AlertPreference>();
  for (const row of value) {
    const pref = normalizeAlertPreference(row);
    if (pref !== null && !byKind.has(pref.eventKind)) byKind.set(pref.eventKind, pref);
  }
  return ALERT_EVENT_KINDS.flatMap((kind) => {
    const pref = byKind.get(kind);
    return pref ? [pref] : [];
  });
}

export function normalizeAlertPreferences(w: WireResponse): AlertPreferences {
  return {
    defaults: normalizeRows(w.defaults),
    virtualAccounts: w.virtualAccounts.map((e) => ({
      virtualAccountId: e.virtualAccountId,
      name: e.name,
      preferences: normalizeRows(e.preferences),
    })),
  };
}

/**
 * The `PUT` body for a matrix: the person's own rows only — the defaults they set (`DEFAULT`) and
 * each virtual account's overrides (`VIRTUAL_ACCOUNT`). A system default, or a virtual account row
 * that is "Same as default", is left out so it keeps following what it inherits.
 */
export function toAlertPreferenceRows(prefs: AlertPreferences): WireInput[] {
  const row = (virtualAccountId: number | null, pref: AlertPreference): WireInput => {
    const input: WireInput = { eventKind: pref.eventKind, channel: 'EMAIL', cadence: pref.cadence };
    if (virtualAccountId !== null) input.virtualAccountId = virtualAccountId;
    if (pref.eventKind === THRESHOLD_KIND && pref.thresholdMinor !== null) {
      input.thresholdMinor = pref.thresholdMinor;
    }
    return input;
  };
  return [
    ...prefs.defaults.filter((pref) => pref.source === 'DEFAULT').map((pref) => row(null, pref)),
    ...prefs.virtualAccounts.flatMap((entry) =>
      entry.preferences
        .filter((pref) => pref.source === 'VIRTUAL_ACCOUNT')
        .map((pref) => row(entry.virtualAccountId, pref)),
    ),
  ];
}

export const alertPreferencesApi = {
  /** GET /v1/me/alert-preferences. */
  async get(): Promise<AlertPreferences> {
    return normalizeAlertPreferences(
      await request<WireResponse>('GET', '/v1/me/alert-preferences'),
    );
  },
  /** PUT /v1/me/alert-preferences — replaces the caller's rows; answers the new matrix. */
  async replace(prefs: AlertPreferences): Promise<AlertPreferences> {
    const body: WireRequest = { preferences: toAlertPreferenceRows(prefs) };
    return normalizeAlertPreferences(
      await request<WireResponse>('PUT', '/v1/me/alert-preferences', { json: body }),
    );
  },
};
